import Image from "next/image";
import classNames from "classnames";
import NextLink from "next/link";
import { Canvas, Typography, Link } from "@cappelendamm/design-system/react";
import { DigitalServiceItem } from "../types";
import { ListingTags } from "@/search/components/item-listings/ListingTags";
import { ListingItemProps } from "./types";
import { Book } from "iconoir-react";
import { selectCollection } from "./selectors";
import { PricelistPrice } from "@/products/components/PricelistPrice";

export const PRODUCT_ARTICLE_IMAGE_WIDTH = 140 as const;
export const PRODUCT_ARTICLE_IMAGE_HEIGHT = 222 as const;

export function DigitalServiceListingItem({
  item,
  config,
}: ListingItemProps<DigitalServiceItem>) {
  const {
    objectID: sku,
    imageUrl,
    price,
    subjectToVAT,
    name,
    priceLists,
    itemDetails: { binding: format, language, contributorByLine, slug },
  } = item;

  const href = slug ? `/digitale-tjenester/${slug}` : null;
  const collection = selectCollection(item);
  const meta = { format, language };

  return (
    <article
      className={classNames(
        "row-span-[var(--list-row-count)] grid min-w-0 grid-rows-subgrid gap-1",
        { "cursor-pointer": href },
        href
          ? "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]"
          : "",
      )}
    >
      <NextLink
        href={href ?? ""}
        aria-labelledby={`item-${sku}`}
        className={classNames(
          "group relative grid min-w-0 grid-rows-subgrid no-underline",
          config?.fields?.series
            ? "row-span-[calc(var(--list-row-count)-1)]"
            : "row-span-full",
        )}
      >
        <Canvas className="order-1 px-2 py-2 md:px-6 md:py-2">
          <ListingTags
            item={item}
            config={{
              showHighlighted: config?.tags?.highlighted,
              showInformational: config?.tags?.informational,
              showPromoted: config?.tags?.promoted,
              showPublicationDate: config?.tags?.publicationDate,
            }}
          />
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={`Omslag av "${name}"`}
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_HEIGHT}
              className="transition-all sm:group-hover:scale-102 sm:group-hover:shadow-lg"
              quality={85}
            />
          ) : (
            <Book
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_WIDTH}
              className="text-gray-200 transition-transform sm:group-hover:scale-102 sm:group-hover:text-gray-400"
            />
          )}
        </Canvas>
        <Typography
          id={`item-${sku}`}
          as="h3"
          variant="title-md"
          className={classNames("order-3 truncate", {
            "sm:group-hover:underline": href,
          })}
        >
          {name}
        </Typography>
        <Typography
          as="p"
          variant="body-base"
          className="order-4 mt-0 block truncate"
          muted
        >
          {contributorByLine}
        </Typography>
        <div className="order-last mt-2 text-3xl font-bold [&_span]:text-left">
          <PricelistPrice
            originalItemPrice={price}
            subjectToVAT={subjectToVAT}
            pricelists={priceLists ?? []}
          />
        </div>
        {config?.fields?.formatMeta ? (
          <div className="order-5 mt-2 flex gap-2 text-sm [&_span]:first-of-type:font-semibold [&_span:not(:first-of-type)]:border-l [&_span:not(:first-of-type)]:pl-2">
            {Object.entries(meta)
              .filter(([, value]) => Boolean(value))
              .map(([key, value]) => (
                <span key={key}>{value}</span>
              ))}
          </div>
        ) : null}
      </NextLink>
      {config?.fields?.series && collection ? (
        <div className="order-6 truncate text-sm">
          {collection.prefix}
          {collection.href ? (
            <Link
              as={NextLink}
              className="inline p-0 font-semibold"
              href={collection.href}
            >
              {collection.linkName}
            </Link>
          ) : (
            collection.linkName
          )}
        </div>
      ) : null}
    </article>
  );
}
